import { PublicClientApplication, AuthenticationResult } from '@azure/msal-browser';
import SessionStorageTokenService, { SessionStorageToken } from '../../helpers/SessionStorageTokenService';
import { AuthAppResponse, loginRequestParam, msalConfig, tokenRequestParam } from './authentificationConfig';

class AuthentificationServiceCustom {

    private MSAL: PublicClientApplication;
    private signInResponse: AuthAppResponse;
    private initialized: boolean = false;

    constructor(configuration: typeof msalConfig) {
        this.MSAL = new PublicClientApplication(configuration);
        this.signInResponse = { isError: false, errorMsg: null, response: null, token: null };
    }

    async initialize(): Promise<void> {
        if (!this.initialized) {
            await this.MSAL.initialize();
            this.initialized = true;
        }
    }

    async handleRedirectCallback(): Promise<AuthAppResponse | null> {
        await this.initialize();
        try {
            const response = await this.MSAL.handleRedirectPromise();
            if (response) {
                // L'utilisateur vient de se connecter via redirection
                return this.createResponse(response);
            }
            return null;
        } catch (error: any) {
            return this.createResponse(null, true, error.message || 'Error handling redirect');
        }
    }

    private storeToken = (response: AuthenticationResult): SessionStorageToken => {
        // Extraire les rôles du token si présents
        const idTokenClaims = response.account?.idTokenClaims as any;
        const roles = idTokenClaims?.roles || idTokenClaims?.role || [];
        const userRole = Array.isArray(roles) ? (roles.length > 0 ? roles[0] : '') : (roles || '');

        // IMPORTANT: Utiliser l'idToken en priorité (comme MSAL v1)
        // Le backend Richemont valide l'ID token, pas un access token pour une API
        return {
            AccessToken: response.idToken || response.accessToken,
            RefreshToken: null,
            UserName: response.account?.username || '',
            UserFullName: response.account?.name || '',
            UserRoles: userRole
        };
    }

    private createResponse = (response: AuthenticationResult | null = null, isError = false, errorMsg: string | null = null): AuthAppResponse => {
        this.signInResponse.isError = isError;
        this.signInResponse.errorMsg = errorMsg;
        this.signInResponse.response = response;
        if (!isError && response) {
            this.signInResponse.token = this.storeToken(response);
        }
        return this.signInResponse;
    }

    signIn = async (): Promise<void> => {
        await this.initialize();
        await this.MSAL.loginRedirect(loginRequestParam);
    }

    refreshToken = async (): Promise<AuthAppResponse> => {
        await this.initialize();
        const account = this.MSAL.getAllAccounts()[0];

        if (!account) return this.createResponse(null, true, 'No account found');

        try {
            const response = await this.MSAL.acquireTokenSilent({
                ...tokenRequestParam,
                account
            });
            return this.createResponse(response);
        } catch (error: any) {
            // Fallback vers loginRedirect si silent fail
            return this.createResponse(null, true, error.message || 'Error acquiring token silently');
        }
    }

    async logOut(): Promise<void> {
        await this.initialize();
        const account = this.MSAL.getAllAccounts()[0];
        await this.MSAL.logoutRedirect({ account });
        SessionStorageTokenService.clearToken();
        SessionStorageTokenService.clearFilters();
    }

    async getAccount() {
        await this.initialize();
        return this.MSAL.getAllAccounts()[0] || null;
    }
}

const AuthenficationService = new AuthentificationServiceCustom(msalConfig);

export default AuthenficationService;
